import React from 'react';
import { cn } from '@utils/index';
import { BlockedIcon, SelectPlanIcon } from '@components/ui/icons';

interface FormCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  title: string;
  description?: string;
  descriptionClassName?: string;
  index?: number;
  imageSrc?: string;
  iconComponent?: React.ComponentType<{ className?: string; size?: number }>;
  classNameHeader?: string;
}

export const FormCard = ({
  children,
  className,
  classNameHeader,
  title,
  description,
  descriptionClassName,
  index,
  imageSrc,
  iconComponent,
  ...props
}: FormCardProps) => {
  const getDisplayIcon = () => {
    if (description && description.includes('Important Note')) {
      return <BlockedIcon className="xl:w-22 xl:h-22 w-16 h-16 object-contain self-center 3xl:h-[70%]" size={88} />;
    }
    if (iconComponent) {
      const IconComponent = iconComponent;
      return <IconComponent className="xl:w-22 xl:h-22 w-16 h-16 object-contain self-center 3xl:h-[70%]" size={88} />;
    }
    if (imageSrc === '/images/select-plan.svg') {
      return <SelectPlanIcon className="xl:w-22 xl:h-22 w-16 h-16 object-contain self-center 3xl:h-[70%]" size={88} />;
    }
    return null;
  };

  return (
    <article className={cn(className)} {...props}>
      <header
        className={cn(
          'flex flex-row gap-10 mt-5 w-full max-md:gap-3',
          classNameHeader || 'pb-6 border-b border-[#ebe9e1]',
          (index !== undefined || imageSrc) && 'mb-0'
        )}
      >
        {(getDisplayIcon() || index !== undefined) && (
          <>
            {getDisplayIcon() ? (
              getDisplayIcon()
            ) : (
              <div className="shrink-0">
                <span
                  className="text-primary xl:text-9xl text-4xl md:text-7xl leading-[0.6] font-semibold"
                  aria-hidden
                >
                  {index}
                </span>
              </div>
            )}
          </>
        )}

        <div className="flex flex-col w-full">
          <h3 
            className="text-[22px] font-bold mb-2 max-md:leading-6 max-md:mb-4 max-sm:mb-3 animate-in fade-in-0 slide-in-from-bottom-2 duration-300 delay-100"
          >
            {title}
          </h3>
          {description ? (
            <div
              className={cn(
                'xl:text-[15px] text-sm font-normal animate-in fade-in-0 slide-in-from-bottom-2 duration-300 delay-100',
                descriptionClassName
              )}
            >
              {description.split('\n').map((line, index) => (
                <p key={index} className={index > 0 ? 'mt-2' : ''}>
                  {line.split('**').map((part, partIndex) => {
                    if (part.includes('@')) {
                      return (
                        <a
                          key={partIndex}
                          href={`mailto:${part}`}
                          className="text-primary font-semibold hover:underline"
                        >
                          {part}
                        </a>
                      );
                    }
                    if (partIndex % 2 === 1) {
                      return (
                        <span key={partIndex} className="font-bold">
                          {part}
                        </span>
                      );
                    }
                    return part;
                  })}
                </p>
              ))}
            </div>
          ) : (
            children
          )}
        </div>
      </header>

      {description ? children : null}
    </article>
  );
};
